import { TutorialKey } from '../types/Tutorial';

export interface TutorialStep {
  target: string;
  title: string;
  content: string;
}

const storeSteps: TutorialStep[] = [
  {
    target: '[data-tutorial="stores-add-button"]',
    title: 'Add Your Stores',
    content: 'Click here to add the stores and supermarkets you shop at. You can search for stores via Google Places or add them manually.',
  },
  {
    target: '[data-tutorial="stores-search"]',
    title: 'Search Stores',
    content: 'Use the search bar to quickly find stores by name, address, or city.',
  },
  {
    target: '[data-tutorial="stores-list"]',
    title: 'Your Store List',
    content: 'Your stores appear here. Tap a store to view details, or use the edit and delete buttons to manage them.',
  },
];

const grocerySteps: TutorialStep[] = [
  {
    target: '[data-tutorial="groceries-add-button"]',
    title: 'Add Groceries',
    content: 'Add groceries to your database by scanning barcodes, searching the product catalog, or entering details manually.',
  },
  {
    target: '[data-tutorial="groceries-search"]',
    title: 'Search Groceries',
    content: 'Search your groceries by name, brand, category, or barcode.',
  },
  {
    target: '[data-tutorial="groceries-list"]',
    title: 'Your Grocery List',
    content: 'All your saved groceries appear here. Tap any item to view its details, or use the edit and delete buttons.',
  },
];

const priceSteps: TutorialStep[] = [
  {
    target: '[data-tutorial="prices-description"]',
    title: 'Track Prices',
    content: 'This page shows all your groceries with their pricing status. Select a grocery to view and manage prices across your stores.',
  },
  {
    target: '[data-tutorial="prices-search"]',
    title: 'Search Groceries',
    content: 'Filter your groceries by name, brand, or category to find what you need.',
  },
  {
    target: '[data-tutorial="prices-filter"]',
    title: 'Filter by Price Status',
    content: 'Toggle between all groceries, only unpriced items that need pricing, or items that already have prices recorded.',
  },
  {
    target: '[data-tutorial="prices-list"]',
    title: 'Grocery Prices',
    content: 'Items needing prices are highlighted. Tap any grocery to see price history and add new prices at different stores.',
  },
];

const shoppingListSteps: TutorialStep[] = [
  {
    target: '[data-tutorial="lists-new-button"]',
    title: 'Create a Shopping List',
    content: 'Start by creating a new shopping list. Give it a name and optionally a description.',
  },
  {
    target: '[data-tutorial="lists-search"]',
    title: 'Search Lists',
    content: 'Quickly find a shopping list by searching its name or description.',
  },
  {
    target: '[data-tutorial="lists-completed-toggle"]',
    title: 'Show Completed Lists',
    content: 'Toggle this to show or hide completed shopping lists. Completed lists are hidden by default.',
  },
  {
    target: '[data-tutorial="lists-list"]',
    title: 'Your Shopping Lists',
    content: 'Your lists are shown with their status: Open, Active, Paused, or Done. Tap a list to view its items and manage it.',
  },
];

// Going Shopping - Step 1: Select Store
const goingShoppingStoreSteps: TutorialStep[] = [
  {
    target: '[data-tutorial="shopping-step-wizard"]',
    title: 'Going Shopping',
    content: 'Follow the three-step wizard: select a store, pick a shopping list, and start shopping. Your progress is tracked as you go.',
  },
  {
    target: '[data-tutorial="shopping-store-list"]',
    title: 'Select Your Store',
    content: 'Choose the store you are heading to. Prices will be loaded for this store so you can see your estimated total.',
  },
  {
    target: '[data-tutorial="shopping-paused-trips"]',
    title: 'Resume Paused Trips',
    content: 'If you have paused shopping trips, you can resume them here without starting over.',
  },
];

// Going Shopping - Step 2: Select Shopping List
const goingShoppingListSteps: TutorialStep[] = [
  {
    target: '[data-tutorial="shopping-create-list"]',
    title: 'Create a New List',
    content: 'Don\'t have a list ready? Create a new shopping list right here before you start shopping.',
  },
  {
    target: '[data-tutorial="shopping-completed-toggle"]',
    title: 'Show Completed Lists',
    content: 'Toggle this to show or hide completed shopping lists.',
  },
  {
    target: '[data-tutorial="shopping-list-selection"]',
    title: 'Pick a Shopping List',
    content: 'Select the shopping list you want to use for this trip. Active and open lists are shown by default.',
  },
];

// Going Shopping - Step 3: Active Shopping
const goingShoppingShopSteps: TutorialStep[] = [
  {
    target: '[data-tutorial="shopping-totals"]',
    title: 'Shopping Totals',
    content: 'Track your spending as you shop. See how many items you\'ve picked, your current total, and the estimated total for the full list.',
  },
  {
    target: '[data-tutorial="shopping-actions"]',
    title: 'Shopping Actions',
    content: 'Switch stores, scan a receipt, pause your trip, or finish shopping when you\'re done.',
  },
  {
    target: '[data-tutorial="shopping-items"]',
    title: 'Your Shopping Items',
    content: 'Check off items as you pick them. Tap the price button to enter or update prices. Items are sorted with unpicked items first.',
  },
];

const stepsByKey: Record<string, TutorialStep[]> = {
  stores: storeSteps,
  groceries: grocerySteps,
  prices: priceSteps,
  shopping_lists: shoppingListSteps,
  going_shopping_store: goingShoppingStoreSteps,
  going_shopping_list: goingShoppingListSteps,
  going_shopping_shop: goingShoppingShopSteps,
};

export function getTutorialSteps(key: TutorialKey): TutorialStep[] {
  return stepsByKey[key] || [];
}
